// <copyright file="asset-content-tag.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import type { AssetVirtualPathInfo } from '../../api/dealer-api-interface-quote';

/**
 * Builds the `?tag=` cache key for an asset URL from the asset's own content hash.
 *
 * INTENT: quote assets are overwritten in place under a stable path, so the URL is the only thing
 * that can tell a cache the bytes are new.
 *
 * The tag is NOT redundant with the ETag. The server already returns the blob's ETag alongside
 * `Cache-Control: private, no-cache, must-revalidate`, and that is what makes an unchanged asset cost
 * a 304 instead of a re-download. But a URL whose string never changes is never re-requested at all:
 * lit leaves an unchanged `src` attribute alone, so no request is issued and the revalidation never
 * gets a chance to happen. The tag exists to change the URL, and must therefore change if and only if
 * the bytes change.
 *
 * `hash` is the SHA256 the server computes and stores for every asset it writes
 * (AssetVirtualPathInfo.Hash), so it satisfies that exactly. It replaces the quote item's record
 * version, which was previously used here and was wrong in both directions: it bumped on any
 * unrelated item save - forcing a needless full re-download of an unchanged image - and stayed put
 * when an asset was rewritten by a path that does not save the item row.
 *
 * @param fallback used when `hash` is null, i.e. rows written before hash tracking existed. Passing
 * the record version there keeps those items no worse off than before.
 */
export function assetContentTag(
  assetVirtualPaths: { [key: string]: AssetVirtualPathInfo } | null | undefined,
  assetKey: string,
  fallback?: string
): string | undefined {
  return assetVirtualPaths?.[assetKey]?.hash ?? fallback;
}
